/**
 * PluginRegistry - Extensible plugin system for the waypoint editor
 * 
 * This registry allows extending the editor with:
 * - Waypoint transformations (mirror, offset, scale, etc.)
 * - Export formats (URP, custom formats)
 * - Robot types and kinematics
 * - Custom validators
 * - UI extensions
 */

// Import types for JSDoc
// eslint-disable-next-line no-unused-vars
import { Types } from '../types/index.js';

/**
 * @typedef {Object} TransformPlugin
 * @property {string} id - Unique identifier
 * @property {string} name - Display name
 * @property {string} description - Description of what the transform does
 * @property {string} category - Category for grouping (e.g., 'mirror', 'offset', 'custom')
 * @property {function(Types.Waypoint[], Object): Types.Waypoint[]} transform - Transform function
 * @property {Object} [schema] - JSON schema for options validation
 * @property {function(Object): {valid: boolean, errors: string[]}} [validate] - Validation function
 */

/**
 * @typedef {Object} ExportPlugin
 * @property {string} id - Unique identifier
 * @property {string} name - Display name
 * @property {string} extension - File extension (e.g., 'urp', 'json')
 * @property {string} mimeType - MIME type for downloads
 * @property {function(Object, Object): string|Blob} export - Export function
 * @property {function(Object): {valid: boolean, errors: string[]}} [validate] - Validation function
 */

/**
 * @typedef {Object} RobotPlugin
 * @property {string} id - Unique identifier (e.g., 'ur10e', 'ur20')
 * @property {string} name - Display name
 * @property {string} manufacturer - Robot manufacturer
 * @property {Object} dhParameters - DH parameters for kinematics
 * @property {Array<Types.JointLimits>} jointLimits - Joint angle limits
 * @property {string} [meshPath] - Path to robot mesh files
 * @property {function(): Object} [createKinematics] - Factory for kinematics solver
 */

/**
 * @typedef {Object} ValidatorPlugin
 * @property {string} id - Unique identifier
 * @property {string} name - Display name
 * @property {string} description - What is validated
 * @property {'error'|'warning'|'info'} severity - Severity level
 * @property {function(Object, Object): {valid: boolean, messages: string[]}} validate - Validation function
 */

class PluginRegistry {
  constructor() {
    this.transforms = new Map();
    this.exporters = new Map();
    this.robots = new Map();
    this.validators = new Map();
    this.listeners = new Map();
  }

  // =====================
  // Transform Plugins
  // =====================

  /**
   * Register a waypoint transformation plugin
   * @param {TransformPlugin} plugin 
   */
  registerTransform(plugin) {
    this._validatePlugin(plugin, ['id', 'name', 'transform']);
    this.transforms.set(plugin.id, plugin);
    this._emit('transform:registered', plugin);
  }

  /**
   * Get a transform plugin by ID
   * @param {string} id 
   * @returns {TransformPlugin|undefined}
   */
  getTransform(id) {
    return this.transforms.get(id);
  }

  /**
   * Get all transform plugins
   * @returns {Array<TransformPlugin>}
   */
  getAllTransforms() {
    return Array.from(this.transforms.values());
  }

  /**
   * Get transforms by category
   * @param {string} category 
   * @returns {Array<TransformPlugin>}
   */
  getTransformsByCategory(category) {
    return this.getAllTransforms().filter(t => t.category === category);
  }

  /**
   * Apply a transform to waypoints
   * @param {string} transformId 
   * @param {Array} waypoints 
   * @param {Object} options 
   * @returns {Array} Transformed waypoints
   */
  applyTransform(transformId, waypoints, options = {}) {
    const transform = this.transforms.get(transformId);
    if (!transform) {
      throw new Error(`Transform not found: ${transformId}`);
    }
    
    // Validate options if schema exists
    if (transform.validate) {
      const validation = transform.validate(options);
      if (!validation.valid) {
        throw new Error(`Invalid options: ${validation.errors.join(', ')}`);
      }
    }
    
    return transform.transform(waypoints, options);
  }

  // =====================
  // Export Plugins
  // =====================

  /**
   * Register an export format plugin
   * @param {ExportPlugin} plugin 
   */
  registerExporter(plugin) {
    this._validatePlugin(plugin, ['id', 'name', 'extension', 'export']);
    this.exporters.set(plugin.id, plugin);
    this._emit('exporter:registered', plugin);
  }

  /**
   * Get an exporter by ID
   * @param {string} id 
   * @returns {ExportPlugin|undefined}
   */
  getExporter(id) {
    return this.exporters.get(id);
  }

  /**
   * Get all exporters
   * @returns {Array<ExportPlugin>}
   */
  getAllExporters() {
    return Array.from(this.exporters.values());
  }

  /**
   * Export data using a specific exporter
   * @param {string} exporterId 
   * @param {Object} data 
   * @param {Object} options 
   * @returns {string|Blob}
   */
  export(exporterId, data, options = {}) {
    const exporter = this.exporters.get(exporterId);
    if (!exporter) {
      throw new Error(`Exporter not found: ${exporterId}`);
    }
    
    // Validate data if validator exists
    if (exporter.validate) {
      const validation = exporter.validate(data);
      if (!validation.valid) {
        throw new Error(`Invalid data: ${validation.errors.join(', ')}`);
      }
    }
    
    return exporter.export(data, options);
  }

  // =====================
  // Robot Plugins
  // =====================

  /**
   * Register a robot type plugin
   * @param {RobotPlugin} plugin 
   */
  registerRobot(plugin) {
    this._validatePlugin(plugin, ['id', 'name', 'manufacturer']);
    this.robots.set(plugin.id, plugin);
    this._emit('robot:registered', plugin);
  }

  /**
   * Get a robot by ID
   * @param {string} id 
   * @returns {RobotPlugin|undefined}
   */
  getRobot(id) {
    return this.robots.get(id);
  }

  /**
   * Get all robots
   * @returns {Array<RobotPlugin>}
   */
  getAllRobots() {
    return Array.from(this.robots.values());
  }

  /**
   * Get robots by manufacturer
   * @param {string} manufacturer 
   * @returns {Array<RobotPlugin>}
   */
  getRobotsByManufacturer(manufacturer) {
    return this.getAllRobots().filter(r => r.manufacturer === manufacturer);
  }

  // =====================
  // Validator Plugins
  // =====================

  /**
   * Register a validator plugin
   * @param {ValidatorPlugin} plugin 
   */
  registerValidator(plugin) {
    this._validatePlugin(plugin, ['id', 'name', 'validate']);
    this.validators.set(plugin.id, plugin);
    this._emit('validator:registered', plugin);
  }

  /**
   * Get a validator by ID
   * @param {string} id 
   * @returns {ValidatorPlugin|undefined}
   */
  getValidator(id) {
    return this.validators.get(id);
  }

  /**
   * Get all validators
   * @returns {Array<ValidatorPlugin>}
   */
  getAllValidators() {
    return Array.from(this.validators.values());
  }

  /**
   * Run all validators on data
   * @param {Object} data 
   * @param {Object} context 
   * @returns {Array<{id: string, valid: boolean, messages: string[], severity: string}>}
   */
  runAllValidators(data, context = {}) {
    const results = [];
    
    for (const validator of this.validators.values()) {
      try {
        const result = validator.validate(data, context);
        results.push({
          id: validator.id,
          name: validator.name,
          severity: validator.severity || 'error',
          ...result
        });
      } catch (error) {
        results.push({
          id: validator.id,
          name: validator.name,
          severity: 'error',
          valid: false,
          messages: [`Validator error: ${error.message}`]
        });
      }
    }
    
    return results;
  }

  // =====================
  // Event System
  // =====================

  /**
   * Subscribe to registry events
   * @param {string} event 
   * @param {Function} callback 
   * @returns {Function} Unsubscribe function
   */
  on(event, callback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event).add(callback);
    
    return () => {
      this.listeners.get(event).delete(callback);
    };
  }

  /**
   * Emit an event
   * @param {string} event 
   * @param {*} data 
   */
  _emit(event, data) {
    const listeners = this.listeners.get(event);
    if (listeners) {
      for (const callback of listeners) {
        try {
          callback(data);
        } catch (error) {
          console.error(`Error in event listener for ${event}:`, error);
        }
      }
    }
  }

  // =====================
  // Utility Methods
  // =====================

  /**
   * Validate that a plugin has required fields
   * @param {Object} plugin 
   * @param {Array<string>} requiredFields 
   */
  _validatePlugin(plugin, requiredFields) {
    for (const field of requiredFields) {
      if (!plugin[field]) {
        throw new Error(`Plugin missing required field: ${field}`);
      }
    }
  }

  /**
   * Unregister a plugin
   * @param {string} type - 'transform' | 'exporter' | 'robot' | 'validator'
   * @param {string} id 
   */
  unregister(type, id) {
    const map = {
      transform: this.transforms,
      exporter: this.exporters,
      robot: this.robots,
      validator: this.validators
    }[type];
    
    if (map && map.has(id)) {
      map.delete(id);
      this._emit(`${type}:unregistered`, { id });
    }
  }

  /**
   * Clear all plugins (useful for testing)
   */
  clear() {
    this.transforms.clear();
    this.exporters.clear();
    this.robots.clear();
    this.validators.clear();
    this._emit('cleared', {});
  }

  /**
   * Get registry statistics
   * @returns {Object}
   */
  getStats() {
    return {
      transforms: this.transforms.size,
      exporters: this.exporters.size,
      robots: this.robots.size,
      validators: this.validators.size
    };
  }
}

// Singleton instance
export const pluginRegistry = new PluginRegistry();

// Export class for testing
export { PluginRegistry };
